import { Badge, Box, Button, Flex, Text } from '@chakra-ui/react'
import {
  Encounter,
  getDateString,
  PrenatalExamData,
  VitalSource,
  VitalSourceKey,
  WithId,
} from '@hb/shared'
import { ReactNode } from 'react'
import { GrayFlaskIcon, OrangeFlaskIcon } from '../../../../icons/FlaskIcon'
import {
  formatBp,
  formatGa,
  getEncounterPresentingComplaint,
  isBpFlagged,
  isFhtFlagged,
  isFundalHeightFlagged,
  isPulseFlagged,
  isTempFlagged,
} from './utils'

// §H: Helper — returns true if the given vital was patient-reported on this encounter.
const isPtReported = (enc: WithId<Encounter>, key: VitalSourceKey): boolean =>
  ((enc.data as any).vitalSources as Partial<Record<VitalSourceKey, VitalSource>> | undefined)?.[
    key
  ] === 'pt-reported'

const CELL_WIDTH = '130px'
const LABEL_WIDTH = '140px'

const labelStyle = {
  fontSize: 'xs' as const,
  fontWeight: 600,
  color: 'gray.500',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  fontFamily: 'Hero-New',
}

interface CellProps {
  text?: string | null
  icon?: ReactNode
  flagged?: boolean
  ptReported?: boolean
  onClick: () => void
}

const GridCell = ({ text, icon, flagged, ptReported, onClick }: CellProps) => (
  <Box
    w={CELL_WIDTH}
    minW={CELL_WIDTH}
    px={2}
    py={1}
    borderRight="1px solid"
    borderColor="gray.100"
    _dark={{ borderColor: 'whiteAlpha.100' }}
    cursor={text || icon ? 'pointer' : 'default'}
    onClick={onClick}
    _hover={text || icon ? { bg: 'blue.50', _dark: { bg: 'blue.900' } } : undefined}>
    {icon ? (
      <Flex align="center" h="100%">
        {icon}
      </Flex>
    ) : (
      <Text
        fontSize="sm"
        fontFamily="Hero-New"
        color={flagged ? 'red.500' : undefined}
        fontWeight={flagged ? 600 : undefined}
        fontStyle={ptReported ? 'italic' : undefined}
        noOfLines={1}>
        {text ?? ''}
      </Text>
    )}
  </Box>
)

const LabelCell = ({ label }: { label: string }) => (
  <Box
    w={LABEL_WIDTH}
    minW={LABEL_WIDTH}
    px={3}
    py={1}
    bg="gray.50"
    _dark={{ bg: 'gray.800' }}
    borderRight="1px solid"
    borderBottom="1px solid"
    borderColor="gray.200"
    position="sticky"
    left={0}
    zIndex={1}>
    <Text {...labelStyle}>{label}</Text>
  </Box>
)

const GridRow = ({
  label,
  encounters,
  getValue,
  onOpen,
  onCellClick,
}: {
  label: string
  encounters: WithId<Encounter>[]
  getValue: (enc: WithId<Encounter>) => {
    text?: string | null
    icon?: ReactNode
    flagged?: boolean
    ptReported?: boolean
  }
  onOpen: (id: string) => void
  onCellClick?: () => void
}) => (
  <Flex borderBottom="1px solid" borderColor="gray.100" _dark={{ borderColor: 'whiteAlpha.100' }}>
    <LabelCell label={label} />
    {encounters.map(enc => {
      const { text, icon, flagged, ptReported } = getValue(enc)
      return (
        <GridCell
          key={enc.id}
          text={text}
          icon={icon}
          flagged={flagged}
          ptReported={ptReported}
          onClick={() => (onCellClick ? onCellClick() : onOpen(enc.id))}
        />
      )
    })}
  </Flex>
)

const fetalPositionLabel: Record<string, string> = {
  cephalic: 'Cephalic',
  'breech-frank': 'Frank breech',
  'breech-complete': 'Complete breech',
  'breech-footling': 'Footling breech',
  transverse: 'Transverse',
  oblique: 'Oblique',
  'not-yet-clinically-relevant': 'N/A',
  breech: 'Breech',
  other: 'Other',
}

const riskLabel: Record<string, string> = { low: 'Low', moderate: 'Moderate', high: 'High' }
const edemaLabel: Record<string, string> = {
  none: 'None',
  trace: 'Trace',
  '1+': '1+',
  '2+': '2+',
  '3+': '3+',
  '4+': '4+',
}
const rvLabel: Record<string, string> = {
  '1-week': '1 week',
  '2-weeks': '2 weeks',
  '4-weeks': '4 weeks',
  prn: 'PRN',
}

const statusColor: Record<string, string> = {
  draft: 'yellow',
  completed: 'blue',
  authenticated: 'green',
}

interface Props {
  encounters: WithId<Encounter>[]
  onOpenEncounter: (id: string) => void
  onOpenLabQuickview?: () => void
  showMore: boolean
  onToggleShowMore: () => void
}

export const FlowSheetGrid = ({
  encounters,
  onOpenEncounter,
  onOpenLabQuickview,
  showMore,
  onToggleShowMore,
}: Props) => {
  if (encounters.length === 0) {
    return (
      <Box p={6} textAlign="center">
        <Text color="gray.400" fontFamily="Hero-New" fontSize="sm">
          No antepartum visits recorded yet.
        </Text>
      </Box>
    )
  }

  const getPreg = (enc: WithId<Encounter>) => enc.data as PrenatalExamData

  const tierARows: Array<{
    label: string
    getValue: (enc: WithId<Encounter>) => {
      text?: string | null
      icon?: ReactNode
      flagged?: boolean
    }
    onCellClick?: () => void
  }> = [
    {
      label: 'Gestational Age',
      getValue: enc => ({ text: formatGa(getPreg(enc).gestationalAge) }),
    },
    {
      label: 'Provider',
      getValue: enc => ({ text: enc.renderingProviderInitials ?? '—' }),
    },
    {
      label: 'Blood Pressure',
      getValue: enc => {
        const bp = enc.data.vital?.bloodPressure
        const ptReported = isPtReported(enc, 'bloodPressure')
        const bpText = formatBp(bp) || undefined
        return {
          text: bpText != null ? `${bpText}${ptReported ? ' P' : ''}` : undefined,
          flagged: isBpFlagged(bp),
          ptReported,
        }
      },
    },
    {
      label: 'Weight',
      getValue: enc => {
        const w = enc.data.vital?.weight
        const bypass = enc.data.vital?.weightBypassAcknowledgedAt
        const ptReported = isPtReported(enc, 'weight')
        return {
          text: w != null ? `${w} lbs${bypass ? ' ▲' : ''}${ptReported ? ' P' : ''}` : undefined,
          ptReported,
        }
      },
    },
    {
      label: 'Fundal Height',
      getValue: enc => {
        const data = getPreg(enc)
        const cm = data.fundalHeightCm
        return {
          text: cm != null ? `${cm} cm` : undefined,
          flagged: isFundalHeightFlagged(cm, data.gestationalAge),
        }
      },
    },
    {
      label: 'FHT (bpm)',
      getValue: enc => {
        const bpm = getPreg(enc).fetalHeartTones
        return { text: bpm != null ? String(bpm) : undefined, flagged: isFhtFlagged(bpm) }
      },
    },
    {
      label: 'Fetal Position',
      getValue: enc => {
        const data = getPreg(enc)
        if ((data.gestationalAge ?? 0) < 196) return { text: undefined }
        const pos = data.fetalPosition
        return { text: pos ? (fetalPositionLabel[pos] ?? pos) : undefined }
      },
    },
    {
      label: 'Edema',
      getValue: enc => {
        const edema = getPreg(enc).edema
        return { text: edema ? edemaLabel[edema] : undefined }
      },
    },
    {
      label: 'Presenting Complaint',
      getValue: enc => ({ text: getEncounterPresentingComplaint(enc) ?? undefined }),
    },
    {
      label: 'Labs',
      getValue: enc => {
        const labs = (enc.data as PrenatalExamData).labs
        const ordered = (labs?.labsOrderedThisVisit?.length ?? 0) > 0
        const reviewed = !!labs?.labReviewConfirmation
        if (!ordered && !reviewed) return {}
        // §0.5.3 Flask icons: orange = reviewed, gray = ordered but not yet confirmed
        const icon = reviewed ? <OrangeFlaskIcon size={20} /> : <GrayFlaskIcon size={20} />
        return { icon }
      },
      onCellClick: onOpenLabQuickview,
    },
  ]

  const tierBRows: typeof tierARows = [
    {
      label: 'Temperature',
      getValue: enc => {
        const t = enc.data.vital?.temperature
        const ptReported = isPtReported(enc, 'temperature')
        return {
          text: t != null ? `${t}°F${ptReported ? ' P' : ''}` : undefined,
          flagged: isTempFlagged(t),
          ptReported,
        }
      },
    },
    {
      label: 'Pulse (bpm)',
      getValue: enc => {
        const p = enc.data.vital?.pulse
        const ptReported = isPtReported(enc, 'pulse')
        return {
          text: p != null ? `${String(p)}${ptReported ? ' P' : ''}` : undefined,
          flagged: isPulseFlagged(p),
          ptReported,
        }
      },
    },
    {
      label: 'GBS Status',
      getValue: enc => {
        const gbs = (enc.data as PrenatalExamData).labs?.gbsStatus
        return { text: gbs ?? undefined }
      },
    },
    {
      label: 'Risk Level',
      getValue: enc => {
        const risk = getPreg(enc).antepartumRisk
        return {
          text: risk ? riskLabel[risk] : undefined,
          flagged: risk === 'high',
        }
      },
    },
    {
      label: 'Next Visit',
      getValue: enc => {
        const rvi = getPreg(enc).returnVisitInterval
        return { text: rvi ? rvLabel[rvi] : undefined }
      },
    },
    {
      label: 'Notes',
      getValue: enc => ({ text: enc.data.notes ? '📝' : undefined }),
    },
  ]

  return (
    <Box overflowX="auto" w="100%">
      {/* Column header row */}
      <Flex
        borderBottom="2px solid"
        borderColor="blue.100"
        position="sticky"
        top={0}
        zIndex={2}
        bg="white"
        _dark={{ borderColor: 'blue.800', bg: 'gray.900' }}>
        <Box
          w={LABEL_WIDTH}
          minW={LABEL_WIDTH}
          px={3}
          py={2}
          bg="gray.50"
          _dark={{ bg: 'gray.800' }}
          position="sticky"
          left={0}
          zIndex={3}>
          <Text {...labelStyle}>Visit</Text>
        </Box>
        {encounters.map(enc => (
          <Box
            key={enc.id}
            w={CELL_WIDTH}
            minW={CELL_WIDTH}
            px={2}
            py={2}
            borderRight="1px solid"
            borderColor="gray.100"
            _dark={{ borderColor: 'whiteAlpha.100' }}
            cursor="pointer"
            onClick={() => onOpenEncounter(enc.id)}
            _hover={{ bg: 'blue.50', _dark: { bg: 'blue.900' } }}>
            <Text fontSize="xs" fontWeight={700} fontFamily="Hero-New" noOfLines={1}>
              {enc.date ? getDateString(enc.date, 'short', true) : '—'}
            </Text>
            <Badge
              mt={0.5}
              fontSize="2xs"
              colorScheme={statusColor[enc.status] ?? 'gray'}
              fontFamily="Hero-New">
              {enc.status}
            </Badge>
          </Box>
        ))}
      </Flex>

      {/* Tier A rows — always visible */}
      {tierARows.map(row => (
        <GridRow
          key={row.label}
          label={row.label}
          encounters={encounters}
          getValue={row.getValue}
          onOpen={onOpenEncounter}
          onCellClick={row.onCellClick}
        />
      ))}

      {/* Show More toggle */}
      <Flex
        borderBottom="1px solid"
        borderColor="gray.200"
        px={3}
        py={1}
        bg="gray.50"
        _dark={{ borderColor: 'whiteAlpha.200', bg: 'gray.800' }}>
        <Button
          size="xs"
          variant="ghost"
          colorScheme="blue"
          fontFamily="Hero-New"
          onClick={onToggleShowMore}>
          {showMore ? 'Show Less' : 'Show More'}
        </Button>
      </Flex>

      {/* Tier B rows — visible when showMore */}
      {showMore &&
        tierBRows.map(row => (
          <GridRow
            key={row.label}
            label={row.label}
            encounters={encounters}
            getValue={row.getValue}
            onOpen={onOpenEncounter}
          />
        ))}

      {/* §H: Flow sheet legend */}
      <Flex
        px={3}
        py={1.5}
        gap={4}
        flexWrap="wrap"
        borderTop="1px solid"
        borderColor="gray.100"
        _dark={{ borderColor: 'whiteAlpha.100' }}>
        <Text fontSize="2xs" color="gray.400" fontFamily="Hero-New">
          ▲ = weight bypass acknowledged
        </Text>
        <Text fontSize="2xs" color="gray.400" fontFamily="Hero-New" fontStyle="italic">
          P = patient self-reported value
        </Text>
      </Flex>
    </Box>
  )
}
